import React, { useState, useEffect } from 'react';
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showDeleteConfirmation, showItemExistsError, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig'; // Adjust path if needed

interface FormErrors {
  featureName: string;
  selectedFeature: string;
}

interface VehicleFeature {
  id: number;
  featureName: string;
}

interface FeatureData {
  status: string;
  data: VehicleFeature[];
  statusDescription?: string;
}

const AddFeatures: React.FC = () => {
  const [featureData, setFeatureData] = useState<FeatureData | null>(null);
  const [isFeatureLoading, setIsFeatureLoading] = useState<boolean>(true);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>("");
  const [newFeatureName, setNewFeatureName] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrors>({
    featureName: "",
    selectedFeature: "",
  });
  const [openFail, setOpenFail] = useState<boolean>(false);


  const handleCloseFail = () => setOpenFail(false);

  // Fetch vehicle features on component mount
  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = () => {
    setIsFeatureLoading(true);
    apiDefinitions.getAllVehicleFeatures()
      .then((response: { data: FeatureData }) => {
        if (response.data.status === "S1000" && response.data.data) {
          setFeatureData(response.data);
        } else {
          setOpenFail(true);
        }
        setIsFeatureLoading(false);
      })
      .catch((error: any) => {
        setOpenFail(true);
        setIsFeatureLoading(false);
      });
  };

  const validateFeatureForm = (name: string): boolean => {
    const errors: FormErrors = { featureName: "", selectedFeature: "" };
    let isValid = true;

    if (!name.trim()) {
      errors.featureName = "Feature name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const validateSelectedFeature = (): boolean => {
    const errors: FormErrors = { featureName: "", selectedFeature: "" };
    let isValid = true;

    if (!selectedFeatureId) {
      errors.selectedFeature = "Please select a feature";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddFeatureSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateFeatureForm(newFeatureName)) return;

    apiDefinitions.addVehicleFeature({ featureName: newFeatureName })
      .then((response: { data: FeatureData }) => {
        if (response.data.status === "S1000") {
          showSuccessAlert("Feature added successfully");
          setNewFeatureName("");
          fetchFeatures();
        } else if (response.data.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A feature with this name already exists.");
        } else {
          setOpenFail(true);
        }
      })
      .catch((error: any) => {
        if (error.response?.data?.statusDescription?.toLowerCase().includes("already exists")) {
          showItemExistsError("A feature with this name already exists.");
        } else {
         
          setOpenFail(true);
        }
      });
  };

  const handleDeleteFeature = () => {
    if (!validateSelectedFeature()) return;

    showDeleteConfirmation(() => {
      apiDefinitions.deleteVehicleFeature(parseInt(selectedFeatureId))
        .then((response: { data: FeatureData }) => {
          if (response.data.status === "S1000") {
            setSelectedFeatureId("");
            fetchFeatures();
          } else {
                     showItemAlreadyUsed("Sorry, this feature is already in use and cannot be deleted.");     
          }
        })
        .catch((error: any) => {
          if (error.response?.data?.statusDescription?.toLowerCase().includes("already used")) {
            showItemAlreadyUsed("Sorry, this feature is already in use and cannot be deleted.");
          } else {
            showItemAlreadyUsed("Sorry, this feature is already in use and cannot be deleted.");
            setOpenFail(true);
          }
        });
    });
  };

  const handleFeatureChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFeatureId(e.target.value);
    setFormErrors((prev) => ({ ...prev, selectedFeature: "" }));
  };

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-20 mb-0">Add Features</h4>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row" style={{ gap: '30px' }}>
                <div className="col-md-5">
                  <div className="mb-4">
                    <label className="col-form-label mb-2"><h5>Feature</h5></label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '300px', padding: '10px 15px' }}
                        value={newFeatureName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFeatureName(e.target.value)}
                        placeholder="Enter feature name"
                      />
                      <button
                        type="button"
                        className="btn btn-success ms-3"
                        data-bs-toggle="modal"
                        data-bs-target="#feature-modal"
                        style={{ padding: '10px 12px', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        Add Feature
                      </button>
                    </div>
                    {formErrors.featureName && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.featureName}</div>
                    )}
                    <select
                      className="form-select mt-3"
                      value={selectedFeatureId}
                      onChange={handleFeatureChange}
                      style={{ width: '300px', padding: '10px' }}
                    >
                      <option value="">Select Feature</option>
                      {isFeatureLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        featureData?.data?.map((feature: VehicleFeature) => (
                          <option key={feature.id} value={feature.id}>{feature.featureName}</option>
                        ))
                      )}
                    </select>
                    {formErrors.selectedFeature && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.selectedFeature}</div>
                    )}
                    <div className="d-flex gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDeleteFeature}
                        style={{ padding: '8px 16px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Feature Modal */}
      <div className="modal fade" id="feature-modal" tabIndex={-1} aria-labelledby="add-feature" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="add-feature">Add New Feature</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddFeatureSubmit}>
                <div className="mb-3">
                  <label htmlFor="newFeatureName" className="form-label">Feature Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newFeatureName"
                    value={newFeatureName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFeatureName(e.target.value)}
                    placeholder="Enter feature name (e.g., Sunroof)"
                    style={{ width: '100%', padding: '10px 15px' }}
                  />
                  {formErrors.featureName && (
                    <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>{formErrors.featureName}</div>
                  )}
                </div>
                <button type="submit" className="btn btn-success" data-bs-dismiss="modal" style={{ padding: '10px 20px' }}>
                  Add Feature
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default AddFeatures;